import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { Button, Container, SectionLabel } from './primitives';
import { BOOKING_URL, SECTION_IDS } from './constants';
import useGoogleAnalytics from '../../hooks/useGoogleAnalytics';

export default function Hero() {
  const { t } = useTranslation();
  const { events: gaEvents } = useGoogleAnalytics();

  const badges = t('home.hero.badges', { returnObjects: true });
  const badgeList = Array.isArray(badges) ? badges : [];

  const exploreClick = (e) => {
    e.preventDefault();
    gaEvents.exploreSolutions('hero');
    const el = document.getElementById(SECTION_IDS.services);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id={SECTION_IDS.hero} className="relative overflow-hidden bg-ink pt-28 pb-20 sm:pt-32 sm:pb-28">
      {/* Backdrop */}
      <div className="pointer-events-none absolute inset-0 bg-grid-faint [background-size:42px_42px] [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]" />
      <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-accent opacity-20 blur-[120px]" />

      <Container className="relative">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          {/* Copy */}
          <div className="text-center lg:text-left">
            <div className="reveal is-visible flex justify-center lg:justify-start">
              <SectionLabel tone="light">{t('home.hero.eyebrow')}</SectionLabel>
            </div>

            <h1 className="mt-6 text-4xl font-extrabold leading-[1.15] tracking-tight text-white sm:text-5xl lg:text-[3.25rem]">
              {t('home.hero.title_line1')}
              <br />
              <span className="bg-accent-gradient bg-clip-text text-transparent">
                {t('home.hero.title_line2')}
              </span>
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-white/60 lg:mx-0">
              {t('home.hero.subtitle')}
            </p>

            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-start">
              <Button
                href={BOOKING_URL}
                external
                variant="primary"
                onClick={() => gaEvents.homeBookDemo('hero')}
              >
                {t('home.hero.cta_primary')}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button href={`#${SECTION_IDS.services}`} variant="secondary" onClick={exploreClick}>
                {t('home.hero.cta_secondary')}
              </Button>
            </div>

            {/* Trust badges */}
            {badgeList.length > 0 && (
              <div className="mt-10 flex flex-wrap items-center justify-center gap-x-5 gap-y-3 lg:justify-start">
                {badgeList.map((badge) => (
                  <span key={badge} className="flex items-center gap-2 text-sm text-white/45">
                    <ShieldCheck className="h-4 w-4 text-accent-400" />
                    {badge}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Visual — transparent animation, so no frame or shadow; the hero
              backdrop shows through. Animated WebP first, GIF as fallback.
              Width-capped so its height stays in step with the copy column. */}
          <div className="reveal is-visible">
            <picture>
              <source srcSet="/easyapplpgifherosection2.webp" type="image/webp" />
              <img
                src="/easyapplpgifherosection2.gif"
                alt={t('home.hero.visual_alt')}
                width={567}
                height={1040}
                className="mx-auto h-auto w-full max-w-[240px] sm:max-w-[280px] lg:max-w-[300px]"
              />
            </picture>
          </div>
        </div>
      </Container>
    </section>
  );
}
